// src/api/features/locations/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ensureLocationTree,
  createCountry,
  updateCountry,
  deleteCountry,
  createRegion,
  updateRegion,
  deleteRegion,
  createCity,
  updateCity,
  deleteCity,
  createCityAlias,
  updateCityAlias,
  deleteCityAlias,
} from "./api";

import type {
  ApiMessage,
  EnsureLocationTreeBody,
  EnsureLocationTreeResult,
  CreateCountryBody,
  UpdateCountryBody,
  CreateRegionBody,
  UpdateRegionBody,
  CreateCityBody,
  UpdateCityBody,
  CreateCityAliasBody,
  UpdateCityAliasBody,
  LocationCountry,
  LocationRegion,
  LocationCity,
  LocationCityAlias,
} from "./types";

import { LocationsKeys } from "./queries";

/* ============================================
 * Helpers
 * ============================================ */

function safeIso2(v: unknown): string {
  return String(v ?? "").trim().toUpperCase();
}

/* ============================================
 * ENSURE LOCATION TREE (admin — POST /locations/ensure)
 * ============================================ */

export function useEnsureLocationTreeMutation() {
  const qc = useQueryClient();

  return useMutation<EnsureLocationTreeResult, unknown, EnsureLocationTreeBody>({
    mutationFn: (payload) => ensureLocationTree(payload),

    onSuccess(_data, vars) {
      // safest: refresh all location queries (dropdowns + admin lists)
      qc.invalidateQueries({ queryKey: LocationsKeys.all });

      // targeted invalidation where we can
      const countryIso2 = safeIso2(vars?.country?.iso2);
      if (countryIso2) {
        qc.invalidateQueries({
          queryKey: LocationsKeys.regions({ countryIso2 }),
        });

        qc.invalidateQueries({
          queryKey: LocationsKeys.cities({ countryIso2 }),
        });
      }
    },
  });
}

/* ============================================
 * COUNTRIES — CRUD mutations
 * ============================================ */

export function useCreateCountryMutation() {
  const qc = useQueryClient();

  return useMutation<LocationCountry, unknown, CreateCountryBody>({
    mutationFn: (payload) => createCountry(payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

export function useUpdateCountryMutation() {
  const qc = useQueryClient();

  return useMutation<
    LocationCountry,
    unknown,
    { iso2: string; body: UpdateCountryBody }
  >({
    mutationFn: ({ iso2, body }) => updateCountry(iso2, body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

export function useDeleteCountryMutation() {
  const qc = useQueryClient();

  return useMutation<ApiMessage, unknown, { iso2: string }>({
    mutationFn: ({ iso2 }) => deleteCountry(iso2),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

/* ============================================
 * REGIONS — CRUD mutations
 * ============================================ */

export function useCreateRegionMutation() {
  const qc = useQueryClient();

  return useMutation<LocationRegion, unknown, CreateRegionBody>({
    mutationFn: (payload) => createRegion(payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

export function useUpdateRegionMutation() {
  const qc = useQueryClient();

  return useMutation<
    LocationRegion,
    unknown,
    { id: number; body: UpdateRegionBody }
  >({
    mutationFn: ({ id, body }) => updateRegion(id, body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

export function useDeleteRegionMutation() {
  const qc = useQueryClient();

  return useMutation<ApiMessage, unknown, { id: number }>({
    mutationFn: ({ id }) => deleteRegion(id),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

/* ============================================
 * CITIES — CRUD mutations
 * ============================================ */

export function useCreateCityMutation() {
  const qc = useQueryClient();

  return useMutation<LocationCity, unknown, CreateCityBody>({
    mutationFn: (payload) => createCity(payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

export function useUpdateCityMutation() {
  const qc = useQueryClient();

  return useMutation<LocationCity, unknown, { id: number; body: UpdateCityBody }>(
    {
      mutationFn: ({ id, body }) => updateCity(id, body),
      onSuccess() {
        qc.invalidateQueries({ queryKey: LocationsKeys.all });
      },
    }
  );
}

export function useDeleteCityMutation() {
  const qc = useQueryClient();

  return useMutation<ApiMessage, unknown, { id: number }>({
    mutationFn: ({ id }) => deleteCity(id),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
    },
  });
}

/* ============================================
 * CITY ALIASES — CRUD mutations
 * ============================================ */

export function useCreateCityAliasMutation() {
  const qc = useQueryClient();

  return useMutation<LocationCityAlias, unknown, CreateCityAliasBody>({
    mutationFn: (payload) => createCityAlias(payload),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
      qc.invalidateQueries({ queryKey: LocationsKeys.cityAliases() });
    },
  });
}

export function useUpdateCityAliasMutation() {
  const qc = useQueryClient();

  return useMutation<
    LocationCityAlias,
    unknown,
    { id: number; body: UpdateCityAliasBody }
  >({
    mutationFn: ({ id, body }) => updateCityAlias(id, body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
      qc.invalidateQueries({ queryKey: LocationsKeys.cityAliases() });
    },
  });
}

export function useDeleteCityAliasMutation() {
  const qc = useQueryClient();

  return useMutation<ApiMessage, unknown, { id: number }>({
    mutationFn: ({ id }) => deleteCityAlias(id),
    onSuccess() {
      qc.invalidateQueries({ queryKey: LocationsKeys.all });
      qc.invalidateQueries({ queryKey: LocationsKeys.cityAliases() });
    },
  });
}
